// NEON CORE interface kit. Every screen is built from these pieces so the game
// reads as one coherent interface: dark translucent chamfered panels with thin
// borders and accent corner ticks, Chakra Petch for headings/numbers, Rajdhani
// for text, glyph hints that follow the active input device, and focus
// navigation that works with keyboard, mouse and gamepad.
//
// Re-exports the smaller UI modules so screens can import everything from here.

import Phaser from 'phaser';
import { FONT, GAME_HEIGHT, GAME_WIDTH, SCENES } from '../config/constants';
import { COLORS, css, mix, tagColor } from '../config/palette';
import type { SfxId, Tag } from '../core/types';
import { iconKey } from '../art/keys';
import { iconTexture, iconTint } from './icons';
import { MenuNav, type NavItem, type NavRect } from './nav';
import { sfx } from './sound';
import { ActionHint, HintBar, type UiAction } from './glyphs';

export * from './nav';
export * from './glyphs';
export * from './icons';
export * from './sound';

// ───────────────────────────── Metrics ─────────────────────────────

export const UI = {
  W: GAME_WIDTH,
  H: GAME_HEIGHT,
  /** Safe margin from the screen edge. */
  margin: 40,
  /** Default chamfer for panels. */
  chamfer: 14,
  panelAlpha: 0.86,
} as const;

/** Type scale tuned for 1920×1080 (body text never below 22 px). */
export const TYPE = {
  hero: 120,
  title: 64,
  h1: 44,
  h2: 32,
  h3: 26,
  body: 24,
  small: 22,
  label: 20,
  micro: 18,
} as const;

// ───────────────────────────── Text ─────────────────────────────

export interface TextOpts {
  size?: number;
  color?: number;
  font?: keyof typeof FONT;
  weight?: 400 | 500 | 600 | 700;
  /** Letter spacing in px. */
  spacing?: number;
  align?: 'left' | 'center' | 'right';
  originX?: number;
  originY?: number;
  /** Word-wrap width. */
  wrap?: number;
  lineSpacing?: number;
  alpha?: number;
  /** Soft glow behind the glyphs (canvas shadow). */
  glow?: { color: number; blur: number; alpha?: number };
  stroke?: { color: number; width: number };
  maxLines?: number;
}

/** Create a text object with the house style. Body text defaults to Rajdhani 600 / 24 px. */
export function txt(scene: Phaser.Scene, x: number, y: number, s: string, o: TextOpts = {}): Phaser.GameObjects.Text {
  const font = o.font ?? 'body';
  const style: Phaser.Types.GameObjects.Text.TextStyle = {
    fontFamily: FONT[font],
    fontSize: `${Math.round(o.size ?? TYPE.body)}px`,
    fontStyle: String(o.weight ?? (font === 'display' ? 600 : 600)),
    color: css(o.color ?? COLORS.uiText),
    align: o.align ?? 'left',
    lineSpacing: o.lineSpacing ?? 0,
  };
  if (o.wrap) style.wordWrap = { width: o.wrap, useAdvancedWrap: true };
  if (o.maxLines) style.maxLines = o.maxLines;
  if (o.spacing) style.letterSpacing = o.spacing;
  const t = scene.add.text(x, y, s, style);
  t.setOrigin(o.originX ?? 0, o.originY ?? 0);
  if (o.glow) t.setShadow(0, 0, css(o.glow.color, o.glow.alpha ?? 1), o.glow.blur, false, true);
  if (o.stroke) t.setStroke(css(o.stroke.color), o.stroke.width);
  if (o.alpha !== undefined) t.setAlpha(o.alpha);
  return t;
}

/** Shorten text with an ellipsis until it fits maxWidth (single line). */
export function fitText(t: Phaser.GameObjects.Text, maxWidth: number): Phaser.GameObjects.Text {
  if (t.width <= maxWidth) return t;
  let s = t.text;
  while (s.length > 1 && t.width > maxWidth) {
    s = s.slice(0, -1);
    t.setText(`${s.trimEnd()}…`);
  }
  return t;
}

/** Shrink the font size (down to minSize) until the text fits maxWidth. */
export function shrinkToFit(t: Phaser.GameObjects.Text, maxWidth: number, minSize = 16): Phaser.GameObjects.Text {
  let size = parseInt(String(t.style.fontSize), 10) || TYPE.body;
  while (t.width > maxWidth && size > minSize) {
    size -= 1;
    t.setFontSize(size);
  }
  return t;
}

// ───────────────────────────── Chamfered shapes ─────────────────────────────

export interface Corners {
  tl?: number;
  tr?: number;
  br?: number;
  bl?: number;
}

function corners(c: number | Corners | undefined): Required<Corners> {
  if (c === undefined) return { tl: UI.chamfer, tr: 0, br: UI.chamfer, bl: 0 };
  if (typeof c === 'number') return { tl: c, tr: 0, br: c, bl: 0 };
  return { tl: c.tl ?? 0, tr: c.tr ?? 0, br: c.br ?? 0, bl: c.bl ?? 0 };
}

/** Outline of a rectangle with cut corners (default: top-left + bottom-right). */
export function chamferPoints(x: number, y: number, w: number, h: number, c?: number | Corners): Phaser.Math.Vector2[] {
  const k = corners(c);
  const lim = Math.min(w, h) / 2;
  const tl = Math.min(k.tl, lim);
  const tr = Math.min(k.tr, lim);
  const br = Math.min(k.br, lim);
  const bl = Math.min(k.bl, lim);
  const V = Phaser.Math.Vector2;
  const pts: Phaser.Math.Vector2[] = [];
  pts.push(new V(x + tl, y));
  pts.push(new V(x + w - tr, y));
  if (tr) pts.push(new V(x + w, y + tr));
  pts.push(new V(x + w, y + h - br));
  if (br) pts.push(new V(x + w - br, y + h));
  pts.push(new V(x + bl, y + h));
  if (bl) pts.push(new V(x, y + h - bl));
  pts.push(new V(x, y + tl));
  return pts;
}

export interface PanelStyle {
  fill?: number;
  fillAlpha?: number;
  border?: number;
  borderAlpha?: number;
  borderWidth?: number;
  chamfer?: number | Corners;
  /** Accent colour for corner ticks / header. */
  accent?: number;
  /** Draw accent strokes along the cut corners. */
  ticks?: boolean;
  /** Outer glow colour (layered translucent strokes). */
  glow?: number;
  glowAlpha?: number;
  /** Inner highlight line under the top edge. */
  topLine?: boolean;
  /** Filled header strip of this height (accent tinted). */
  header?: number;
  headerAlpha?: number;
  /** Faint horizontal scanlines inside the panel. */
  scanlines?: boolean;
}

/** Draw a chamfered panel into a Graphics object (top-left at x,y). */
export function drawPanel(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, s: PanelStyle = {}): void {
  const k = corners(s.chamfer);
  if (s.glow !== undefined) {
    const ga = s.glowAlpha ?? 0.35;
    for (let i = 3; i >= 1; i--) {
      const d = i * 2.5;
      const kc: Corners = { tl: k.tl && k.tl + d * 0.41, tr: k.tr && k.tr + d * 0.41, br: k.br && k.br + d * 0.41, bl: k.bl && k.bl + d * 0.41 };
      g.lineStyle(2.5, s.glow, ga / (i * 1.6));
      g.strokePoints(chamferPoints(x - d, y - d, w + d * 2, h + d * 2, kc), true, true);
    }
  }
  const pts = chamferPoints(x, y, w, h, s.chamfer);
  g.fillStyle(s.fill ?? COLORS.uiPanel, s.fillAlpha ?? UI.panelAlpha);
  g.fillPoints(pts, true);
  if (s.header) {
    const hp = chamferPoints(x, y, w, s.header, { tl: k.tl, tr: k.tr });
    g.fillStyle(s.accent ?? COLORS.uiBorder, s.headerAlpha ?? 0.16);
    g.fillPoints(hp, true);
    g.lineStyle(1, s.accent ?? COLORS.uiBorder, 0.45);
    g.lineBetween(x + 1, y + s.header, x + w - 1, y + s.header);
  }
  if (s.scanlines) {
    g.lineStyle(1, 0xffffff, 0.022);
    for (let yy = y + 3; yy < y + h - 2; yy += 4) g.lineBetween(x + 2, yy, x + w - 2, yy);
  }
  if (s.topLine !== false) {
    g.lineStyle(1, 0xffffff, 0.06);
    g.lineBetween(x + k.tl + 2, y + 2, x + w - k.tr - 2, y + 2);
  }
  g.lineStyle(s.borderWidth ?? 1.5, s.border ?? COLORS.uiBorder, s.borderAlpha ?? 1);
  g.strokePoints(pts, true, true);
  if (s.ticks && s.accent !== undefined) drawCornerTicks(g, x, y, w, h, s.chamfer, s.accent);
}

/** Bright accent strokes along each cut corner plus short runs along the adjoining edges. */
export function drawCornerTicks(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, c: number | Corners | undefined, color: number, alpha = 1): void {
  const k = corners(c);
  const run = 18;
  g.lineStyle(2.5, color, alpha);
  if (k.tl) {
    g.beginPath();
    g.moveTo(x, y + k.tl + run);
    g.lineTo(x, y + k.tl);
    g.lineTo(x + k.tl, y);
    g.lineTo(x + k.tl + run, y);
    g.strokePath();
  }
  if (k.tr) {
    g.beginPath();
    g.moveTo(x + w - k.tr - run, y);
    g.lineTo(x + w - k.tr, y);
    g.lineTo(x + w, y + k.tr);
    g.lineTo(x + w, y + k.tr + run);
    g.strokePath();
  }
  if (k.br) {
    g.beginPath();
    g.moveTo(x + w, y + h - k.br - run);
    g.lineTo(x + w, y + h - k.br);
    g.lineTo(x + w - k.br, y + h);
    g.lineTo(x + w - k.br - run, y + h);
    g.strokePath();
  }
  if (k.bl) {
    g.beginPath();
    g.moveTo(x + k.bl + run, y + h);
    g.lineTo(x + k.bl, y + h);
    g.lineTo(x, y + h - k.bl);
    g.lineTo(x, y + h - k.bl - run);
    g.strokePath();
  }
}

/** Focus brackets: four L-shaped corners just outside a box. */
export function drawBrackets(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, color: number, len = 14, pad = 6, width = 2, alpha = 1): void {
  const x0 = x - pad;
  const y0 = y - pad;
  const x1 = x + w + pad;
  const y1 = y + h + pad;
  g.lineStyle(width, color, alpha);
  g.beginPath();
  g.moveTo(x0, y0 + len);
  g.lineTo(x0, y0);
  g.lineTo(x0 + len, y0);
  g.moveTo(x1 - len, y0);
  g.lineTo(x1, y0);
  g.lineTo(x1, y0 + len);
  g.moveTo(x1, y1 - len);
  g.lineTo(x1, y1);
  g.lineTo(x1 - len, y1);
  g.moveTo(x0 + len, y1);
  g.lineTo(x0, y1);
  g.lineTo(x0, y1 - len);
  g.strokePath();
}

/** A panel as a positioned Graphics object (top-left at x,y) that can be redrawn. */
export class Panel extends Phaser.GameObjects.Graphics {
  private style: PanelStyle;
  panelW: number;
  panelH: number;

  constructor(scene: Phaser.Scene, x: number, y: number, w: number, h: number, style: PanelStyle = {}) {
    super(scene, { x, y });
    this.panelW = w;
    this.panelH = h;
    this.style = style;
    scene.add.existing(this);
    this.redraw();
  }

  setStyle(patch: Partial<PanelStyle>): this {
    this.style = { ...this.style, ...patch };
    return this.redraw();
  }

  resize(w: number, h: number): this {
    this.panelW = w;
    this.panelH = h;
    return this.redraw();
  }

  redraw(): this {
    this.clear();
    drawPanel(this, 0, 0, this.panelW, this.panelH, this.style);
    return this;
  }
}

export function createPanel(scene: Phaser.Scene, x: number, y: number, w: number, h: number, style: PanelStyle = {}): Panel {
  return new Panel(scene, x, y, w, h, style);
}

/** Section heading: small spaced caps with an accent bar and a hairline. Returns the bottom y. */
export function sectionHeader(scene: Phaser.Scene, parent: Phaser.GameObjects.Container | null, x: number, y: number, w: number, label: string, accent: number = COLORS.uiDim): number {
  const g = scene.add.graphics();
  g.fillStyle(accent, 1);
  g.fillRect(x, y + 4, 4, 18);
  const t = txt(scene, x + 14, y, label.toUpperCase(), { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 3, weight: 600 });
  g.lineStyle(1, COLORS.uiBorder, 1);
  g.lineBetween(x + 24 + t.width, y + 13, x + w, y + 13);
  parent?.add([g, t]);
  return y + 34;
}

/** Full-screen dim layer for overlays; blocks pointer input to scenes below. */
export function backdrop(scene: Phaser.Scene, alpha = 0.72, color: number = COLORS.uiBg): Phaser.GameObjects.Rectangle {
  const r = scene.add.rectangle(0, 0, UI.W, UI.H, color, alpha).setOrigin(0, 0);
  r.setInteractive();
  return r;
}

/** Soft radial vignette texture (transparent centre, `color` edges). Cached by key. */
export function vignetteTexture(scene: Phaser.Scene, key: string, color: number, inner = 0.45, outerAlpha = 1): string {
  if (scene.textures.exists(key)) return key;
  const w = 480;
  const h = 270;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return '__DEFAULT';
  const c = tex.getContext();
  const grad = c.createRadialGradient(w / 2, h / 2, Math.min(w, h) * inner * 0.5, w / 2, h / 2, Math.hypot(w, h) / 2);
  grad.addColorStop(0, css(color, 0));
  grad.addColorStop(0.55, css(color, outerAlpha * 0.35));
  grad.addColorStop(1, css(color, outerAlpha));
  c.fillStyle = grad;
  c.fillRect(0, 0, w, h);
  tex.refresh();
  return key;
}

/** Soft round glow texture (white), for tinted additive glows. */
export function glowTexture(scene: Phaser.Scene): string {
  const key = 'ui_glow';
  if (scene.textures.exists(key)) return key;
  const s = 128;
  const tex = scene.textures.createCanvas(key, s, s);
  if (!tex) return '__DEFAULT';
  const c = tex.getContext();
  const grad = c.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.45)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  c.fillStyle = grad;
  c.fillRect(0, 0, s, s);
  tex.refresh();
  return key;
}

// ───────────────────────────── Icons ─────────────────────────────

/**
 * Icon image fitted into a box of `size` px (keeps aspect). Resolves missing
 * textures to generated pictograms; tints white pictograms with `color`.
 */
export function iconImage(scene: Phaser.Scene, x: number, y: number, key: string, size: number, color?: number, frame?: string | number): Phaser.GameObjects.Image {
  const k = iconTexture(scene, key);
  const img = scene.add.image(x, y, k, k === key ? frame : undefined);
  const fw = img.frame.width || 1;
  const fh = img.frame.height || 1;
  img.setScale(Math.min(size / fw, size / fh));
  if (color !== undefined) img.setTint(iconTint(scene, k, color));
  return img;
}

/** Fit an existing image inside w×h. */
export function fitImage(img: Phaser.GameObjects.Image, w: number, h: number): Phaser.GameObjects.Image {
  const fw = img.frame.width || 1;
  const fh = img.frame.height || 1;
  img.setScale(Math.min(w / fw, h / fh));
  return img;
}

// ───────────────────────────── Buttons ─────────────────────────────

export interface ButtonOptions {
  label: string;
  width?: number;
  height?: number;
  /** Small right-aligned text (price, value, state). */
  sublabel?: string;
  sublabelColor?: number;
  /** Icon texture key (falls back to a generated pictogram). */
  icon?: string;
  iconColor?: number;
  accent?: number;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  fontSize?: number;
  align?: 'left' | 'center';
  disabled?: boolean;
  /** Activation sound (default ui_click; primary → ui_confirm). */
  sound?: SfxId | null;
  /** Glyph shown at the right edge (e.g. 'launch' on the big button). */
  hint?: UiAction;
  onClick?: () => void;
}

/** Focusable chamfered button. Container centred on (x, y). */
export class UIButton extends Phaser.GameObjects.Container implements NavItem {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly labelText: Phaser.GameObjects.Text;
  private subText: Phaser.GameObjects.Text | null = null;
  private icon: Phaser.GameObjects.Image | null = null;
  private hintGlyph: ActionHint | null = null;
  private opts: ButtonOptions;
  private focusedFlag = false;
  private focusT = 0;
  private pressed = false;
  private disabledFlag: boolean;
  private focusTween: Phaser.Tweens.Tween | null = null;
  readonly bw: number;
  readonly bh: number;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: ButtonOptions) {
    super(scene, x, y);
    this.opts = opts;
    this.bw = opts.width ?? 360;
    this.bh = opts.height ?? 64;
    this.disabledFlag = !!opts.disabled;
    this.g = scene.add.graphics();
    this.add(this.g);
    const align = opts.align ?? 'left';
    const pad = 28;
    const iconGap = 14;
    const isz = opts.icon ? Math.min(this.bh - 22, 40) : 0;
    this.labelText = txt(scene, 0, 0, opts.label.toUpperCase(), {
      font: 'display',
      size: opts.fontSize ?? (this.bh >= 70 ? 30 : TYPE.h3 - 2),
      weight: 600,
      spacing: 2,
      originX: align === 'left' ? 0 : 0.5,
      originY: 0.5,
    });
    let iconX = -this.bw / 2 + pad + isz / 2;
    if (align === 'left') {
      this.labelText.x = -this.bw / 2 + pad + (opts.icon ? isz + iconGap : 0);
    } else if (opts.icon) {
      // Centre the icon + label as one group.
      const group = isz + iconGap + this.labelText.width;
      iconX = -group / 2 + isz / 2;
      this.labelText.x = -group / 2 + isz + iconGap + this.labelText.width / 2;
    }
    if (opts.icon) {
      this.icon = iconImage(scene, iconX, 0, opts.icon, isz, opts.iconColor ?? COLORS.uiText);
      this.add(this.icon);
    }
    this.add(this.labelText);
    if (opts.sublabel !== undefined) this.setSublabel(opts.sublabel, opts.sublabelColor);
    if (opts.hint) {
      this.hintGlyph = new ActionHint(scene, opts.hint, '', { size: 28 });
      this.hintGlyph.setPosition(this.bw / 2 - 26 - this.hintGlyph.width, 0);
      this.add(this.hintGlyph);
    }
    scene.add.existing(this);
    this.setSize(this.bw, this.bh);
    this.setInteractive();
    this.redraw();
  }

  get accent(): number {
    if (this.opts.variant === 'danger') return COLORS.uiBad;
    return this.opts.accent ?? COLORS.volt;
  }

  setLabel(s: string): this {
    this.labelText.setText(s.toUpperCase());
    return this;
  }

  setSublabel(s: string, color?: number): this {
    if (!this.subText) {
      this.subText = txt(this.scene, this.bw / 2 - 26, 0, '', { font: 'display', size: TYPE.label, weight: 600, originX: 1, originY: 0.5, spacing: 1 });
      this.add(this.subText);
    }
    this.subText.setText(s);
    if (color !== undefined) this.opts.sublabelColor = color;
    this.redraw();
    return this;
  }

  setAccent(color: number): this {
    this.opts.accent = color;
    return this.redraw();
  }

  setDisabled(on: boolean): this {
    this.disabledFlag = on;
    return this.redraw();
  }

  get disabled(): boolean {
    return this.disabledFlag;
  }

  setOnClick(fn: () => void): this {
    this.opts.onClick = fn;
    return this;
  }

  // NavItem
  navBounds(): NavRect {
    const m = this.getWorldTransformMatrix();
    return { x: m.tx, y: m.ty, w: this.bw * m.scaleX, h: this.bh * m.scaleY };
  }

  isEnabled(): boolean {
    return !this.disabledFlag;
  }

  isFocusable(): boolean {
    return this.visible && this.active;
  }

  setFocused(on: boolean): void {
    if (on === this.focusedFlag) return;
    this.focusedFlag = on;
    this.focusTween?.stop();
    this.focusTween = this.scene.tweens.add({
      targets: this,
      focusT: on ? 1 : 0,
      duration: on ? 130 : 160,
      ease: 'Cubic.easeOut',
      onUpdate: () => this.redraw(),
    });
  }

  activate(): void {
    if (this.disabledFlag) {
      sfx('ui_deny');
      this.shake();
      return;
    }
    const snd = this.opts.sound === undefined ? (this.opts.variant === 'primary' ? 'ui_confirm' : 'ui_click') : this.opts.sound;
    if (snd) sfx(snd);
    this.scene.tweens.add({ targets: this, scale: { from: 0.96, to: 1 }, duration: 160, ease: 'Back.easeOut' });
    this.opts.onClick?.();
  }

  /** Short horizontal shake (denied action). */
  shake(): void {
    const x0 = this.x;
    this.scene.tweens.add({
      targets: this,
      x: { from: x0 - 6, to: x0 },
      duration: 220,
      ease: 'Elastic.easeOut',
      onComplete: () => (this.x = x0),
    });
  }

  /** Visual press state for mouse down. */
  setPressed(on: boolean): void {
    this.pressed = on;
    this.setScale(on ? 0.975 : 1);
  }

  redraw(): this {
    const g = this.g;
    const w = this.bw;
    const h = this.bh;
    const t = this.focusT;
    const a = this.accent;
    const disabled = this.disabledFlag;
    const primary = this.opts.variant === 'primary';
    const ghost = this.opts.variant === 'ghost';
    const ch = Math.min(14, h * 0.28);
    g.clear();
    const x = -w / 2;
    const y = -h / 2;
    const baseFill = primary ? mix(COLORS.uiPanel, a, disabled ? 0.06 : 0.2) : COLORS.uiPanel;
    const fill = mix(baseFill, a, disabled ? 0.03 : t * 0.22);
    if (t > 0.01 && !disabled) {
      g.fillStyle(a, 0.08 * t);
      g.fillPoints(chamferPoints(x - 6, y - 6, w + 12, h + 12, ch + 3), true);
    }
    g.fillStyle(fill, ghost ? 0.4 + t * 0.4 : 0.9);
    g.fillPoints(chamferPoints(x, y, w, h, ch), true);
    const border = disabled ? COLORS.uiBorder : mix(primary ? mix(COLORS.uiBorder, a, 0.7) : COLORS.uiBorder, a, t);
    g.lineStyle(t > 0.5 || primary ? 2 : 1.5, border, disabled ? 0.6 : 1);
    g.strokePoints(chamferPoints(x, y, w, h, ch), true, true);
    if (t > 0.01) {
      drawCornerTicks(g, x, y, w, h, ch, disabled ? COLORS.uiDim : a, t);
    }
    const labelColor = disabled ? COLORS.uiDim : t > 0.5 || primary ? 0xffffff : COLORS.uiText;
    this.labelText.setColor(css(labelColor));
    this.labelText.setAlpha(disabled ? 0.55 : 1);
    if ((this.opts.align ?? 'left') === 'left') {
      const base = -w / 2 + 28 + (this.icon ? Math.min(h - 22, 40) + 14 : 0);
      this.labelText.x = base + t * 6;
    }
    if (this.icon) {
      this.icon.setAlpha(disabled ? 0.45 : 0.85 + 0.15 * t);
    }
    if (this.subText) {
      this.subText.setColor(css(disabled ? COLORS.uiDim : (this.opts.sublabelColor ?? COLORS.uiDim)));
      this.subText.setAlpha(disabled ? 0.8 : 1);
    }
    if (this.hintGlyph) this.hintGlyph.setAlpha(disabled ? 0.35 : 0.55 + 0.45 * t);
    void this.pressed;
    return this;
  }
}

/** Create a button, register it with a nav (optional) and wire mouse input. */
export function createButton(scene: Phaser.Scene, x: number, y: number, opts: ButtonOptions, nav?: MenuNav): UIButton {
  const b = new UIButton(scene, x, y, opts);
  if (nav) {
    nav.add(b);
    nav.bindPointer(b, b, (down) => b.setPressed(down));
  } else {
    b.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, () => b.setFocused(true));
    b.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => b.setFocused(false));
    b.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, () => b.activate());
  }
  return b;
}

// ───────────────────────────── Option rows (settings) ─────────────────────────────

abstract class OptionRow extends Phaser.GameObjects.Container implements NavItem {
  protected readonly bg: Phaser.GameObjects.Graphics;
  protected readonly labelText: Phaser.GameObjects.Text;
  protected focusedFlag = false;
  protected focusT = 0;
  readonly rw: number;
  readonly rh: number;
  protected accent: number;

  constructor(scene: Phaser.Scene, x: number, y: number, w: number, h: number, label: string, accent: number) {
    super(scene, x, y);
    this.rw = w;
    this.rh = h;
    this.accent = accent;
    this.bg = scene.add.graphics();
    this.labelText = txt(scene, -w / 2 + 34, 0, label.toUpperCase(), { font: 'display', size: TYPE.h3 - 2, weight: 600, spacing: 2, originY: 0.5 });
    this.add([this.bg, this.labelText]);
    scene.add.existing(this);
    this.setSize(w, h);
    this.setInteractive();
  }

  navBounds(): NavRect {
    const m = this.getWorldTransformMatrix();
    return { x: m.tx, y: m.ty, w: this.rw, h: this.rh };
  }

  setFocused(on: boolean): void {
    if (on === this.focusedFlag) return;
    this.focusedFlag = on;
    this.scene.tweens.add({ targets: this, focusT: on ? 1 : 0, duration: 120, onUpdate: () => this.redraw() });
  }

  abstract activate(): void;
  abstract adjust(dir: -1 | 1): boolean;

  protected drawRow(): void {
    const g = this.bg;
    const w = this.rw;
    const h = this.rh;
    const t = this.focusT;
    g.clear();
    g.fillStyle(mix(COLORS.uiPanel, this.accent, 0.16 * t), 0.35 + 0.55 * t);
    g.fillPoints(chamferPoints(-w / 2, -h / 2, w, h, 10), true);
    if (t > 0.01) {
      g.lineStyle(1.5, this.accent, 0.8 * t);
      g.strokePoints(chamferPoints(-w / 2, -h / 2, w, h, 10), true, true);
      g.fillStyle(this.accent, 1);
      g.fillRect(-w / 2 + 8, -h / 2 + 10, 4, (h - 20) * t);
    } else {
      g.lineStyle(1, COLORS.uiBorder, 0.6);
      g.lineBetween(-w / 2 + 20, h / 2, w / 2 - 20, h / 2);
    }
    this.labelText.setColor(css(t > 0.5 ? 0xffffff : COLORS.uiText));
  }

  abstract redraw(): void;
}

export interface SliderOptions {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  format?: (v: number) => string;
  onChange: (v: number) => void;
  accent?: number;
}

/** Label + segmented slider + value. Left/right adjust; click or drag the track. */
export class SliderRow extends OptionRow {
  private value: number;
  private readonly o: Required<Omit<SliderOptions, 'accent'>>;
  private readonly track: Phaser.GameObjects.Graphics;
  private readonly valueText: Phaser.GameObjects.Text;
  private readonly trackW = 360;
  private dragging = false;

  constructor(scene: Phaser.Scene, x: number, y: number, w: number, h: number, label: string, opts: SliderOptions) {
    super(scene, x, y, w, h, label, opts.accent ?? COLORS.volt);
    this.o = { min: 0, max: 1, step: 0.05, format: (v) => `${Math.round(v * 100)}%`, ...opts };
    this.value = Phaser.Math.Clamp(opts.value, this.o.min, this.o.max);
    this.track = scene.add.graphics();
    this.valueText = txt(scene, w / 2 - 34, 0, '', { font: 'display', size: TYPE.h3 - 2, weight: 600, originX: 1, originY: 0.5 });
    this.add([this.track, this.valueText]);
    const trackX = this.trackLeft();
    const hit = scene.add.zone(trackX + this.trackW / 2, 0, this.trackW + 24, h).setInteractive();
    this.add(hit);
    hit.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      this.dragging = true;
      this.setFromPointer(p);
    });
    scene.input.on(Phaser.Input.Events.POINTER_MOVE, (p: Phaser.Input.Pointer) => {
      if (this.dragging && p.isDown) this.setFromPointer(p);
    });
    scene.input.on(Phaser.Input.Events.POINTER_UP, () => (this.dragging = false));
    this.redraw();
  }

  private trackLeft(): number {
    return this.rw / 2 - 150 - this.trackW;
  }

  private setFromPointer(p: Phaser.Input.Pointer): void {
    const m = this.getWorldTransformMatrix();
    const local = p.x - m.tx - this.trackLeft();
    const t = Phaser.Math.Clamp(local / this.trackW, 0, 1);
    const raw = this.o.min + t * (this.o.max - this.o.min);
    this.set(Math.round(raw / this.o.step) * this.o.step, true);
  }

  set(v: number, notify = false): void {
    const nv = Phaser.Math.Clamp(Number(v.toFixed(4)), this.o.min, this.o.max);
    if (nv === this.value) return;
    this.value = nv;
    this.redraw();
    if (notify) {
      sfx('ui_hover', { pitch: 0.8 + 0.5 * ((nv - this.o.min) / (this.o.max - this.o.min || 1)) });
      this.o.onChange(nv);
    }
  }

  get current(): number {
    return this.value;
  }

  activate(): void {
    // Confirm on a slider nudges it forward and wraps (gamepad-friendly).
    if (this.value >= this.o.max) this.set(this.o.min, true);
    else this.set(this.value + this.o.step, true);
  }

  adjust(dir: -1 | 1): boolean {
    this.set(this.value + dir * this.o.step, true);
    return true;
  }

  redraw(): void {
    this.drawRow();
    const g = this.track;
    g.clear();
    const x0 = this.trackLeft();
    const n = 20;
    const gap = 3;
    const segW = (this.trackW - gap * (n - 1)) / n;
    const t = (this.value - this.o.min) / (this.o.max - this.o.min || 1);
    const filled = t * n;
    for (let i = 0; i < n; i++) {
      const sx = x0 + i * (segW + gap);
      const f = Phaser.Math.Clamp(filled - i, 0, 1);
      g.fillStyle(COLORS.steel1, 1);
      g.fillRect(sx, -9, segW, 18);
      if (f > 0) {
        g.fillStyle(mix(this.accent, 0xffffff, 0.15 * this.focusT), 1);
        g.fillRect(sx, -9, segW * f, 18);
      }
    }
    // Thumb.
    const tx = x0 + t * this.trackW;
    g.fillStyle(0xffffff, 1);
    g.fillRect(tx - 2, -15, 4, 30);
    this.valueText.setText(this.o.format(this.value));
    this.valueText.setColor(css(this.focusT > 0.5 ? 0xffffff : COLORS.uiText));
  }
}

/** Label + ON/OFF switch. Confirm or left/right toggles. */
export class ToggleRow extends OptionRow {
  private value: boolean;
  private readonly sw: Phaser.GameObjects.Graphics;
  private readonly stateText: Phaser.GameObjects.Text;
  private readonly onChange: (v: boolean) => void;
  private knob = 0;
  private lastActivate = -1e9;

  constructor(scene: Phaser.Scene, x: number, y: number, w: number, h: number, label: string, value: boolean, onChange: (v: boolean) => void, accent: number = COLORS.volt) {
    super(scene, x, y, w, h, label, accent);
    this.value = value;
    this.knob = value ? 1 : 0;
    this.onChange = onChange;
    this.sw = scene.add.graphics();
    this.stateText = txt(scene, w / 2 - 34, 0, '', { font: 'display', size: TYPE.h3 - 2, weight: 600, originX: 1, originY: 0.5 });
    this.add([this.sw, this.stateText]);
    this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, () => this.activate());
    this.redraw();
  }

  get current(): boolean {
    return this.value;
  }

  set(v: boolean, notify = false): void {
    if (v === this.value) return;
    this.value = v;
    this.scene.tweens.add({ targets: this, knob: v ? 1 : 0, duration: 140, ease: 'Cubic.easeOut', onUpdate: () => this.redraw() });
    this.redraw();
    if (notify) {
      sfx('ui_click');
      this.onChange(v);
    }
  }

  activate(): void {
    // One click can reach this twice (the row's own pointer handler and a MenuNav bound to
    // it); without this guard the second call flipped the switch straight back.
    const now = performance.now();
    if (now - this.lastActivate < 120) return;
    this.lastActivate = now;
    this.set(!this.value, true);
  }

  adjust(dir: -1 | 1): boolean {
    this.set(dir > 0, true);
    return true;
  }

  redraw(): void {
    this.drawRow();
    const g = this.sw;
    g.clear();
    const w = 84;
    const h = 32;
    const x = this.rw / 2 - 150 - w;
    const on = this.knob;
    g.fillStyle(mix(COLORS.steel1, this.accent, 0.55 * on), 1);
    g.fillRoundedRect(x, -h / 2, w, h, h / 2);
    g.lineStyle(1.5, mix(COLORS.uiBorder, this.accent, on), 1);
    g.strokeRoundedRect(x, -h / 2, w, h, h / 2);
    g.fillStyle(on > 0.5 ? 0xffffff : COLORS.steel4, 1);
    g.fillCircle(x + h / 2 + (w - h) * on, 0, h / 2 - 5);
    this.stateText.setText(this.value ? 'ON' : 'OFF');
    this.stateText.setColor(css(this.value ? this.accent : COLORS.uiDim));
  }
}

// ───────────────────────────── Bars ─────────────────────────────

export interface SegmentedBarOptions {
  color: number;
  /** Value covered by one segment (e.g. 10 HP). 0 = no segment marks. */
  segment?: number;
  bg?: number;
  /** Colour of the lagging "recent damage" trail. */
  ghostColor?: number;
  chamfer?: number;
}

/**
 * Horizontal bar with segment marks and a lagging damage trail. Container
 * origin = top-left. Call set() as often as you like; it only redraws on change.
 */
export class SegmentedBar extends Phaser.GameObjects.Container {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly o: SegmentedBarOptions;
  private value = 0;
  private max = 1;
  private ghost = 0;
  private ghostHold = 0;
  private flashT = 0;
  private flashColor = 0xffffff;
  private dirty = true;
  readonly barW: number;
  readonly barH: number;

  constructor(scene: Phaser.Scene, x: number, y: number, w: number, h: number, opts: SegmentedBarOptions) {
    super(scene, x, y);
    this.barW = w;
    this.barH = h;
    this.o = opts;
    this.g = scene.add.graphics();
    this.add(this.g);
    scene.add.existing(this);
  }

  setColor(c: number): this {
    if (c !== this.o.color) {
      this.o.color = c;
      this.dirty = true;
    }
    return this;
  }

  set(value: number, max: number, instant = false): this {
    const v = Math.max(0, value);
    const m = Math.max(1, max);
    if (v === this.value && m === this.max) return this;
    if (v < this.value && !instant) {
      // Damage: keep the ghost where the bar was, hold, then drain.
      this.ghost = Math.max(this.ghost, this.value);
      this.ghostHold = 0.42;
    } else if (instant || v > this.ghost) {
      this.ghost = v;
    }
    this.value = v;
    this.max = m;
    this.dirty = true;
    return this;
  }

  flash(color = 0xffffff, seconds = 0.35): void {
    this.flashT = seconds;
    this.flashColor = color;
    this.dirty = true;
  }

  get fraction(): number {
    return this.value / this.max;
  }

  tick(dt: number): void {
    if (this.ghost > this.value) {
      if (this.ghostHold > 0) this.ghostHold -= dt;
      else this.ghost = Math.max(this.value, this.ghost - this.max * dt * 0.9);
      this.dirty = true;
    }
    if (this.flashT > 0) {
      this.flashT = Math.max(0, this.flashT - dt);
      this.dirty = true;
    }
    if (this.dirty) this.redraw();
  }

  private redraw(): void {
    this.dirty = false;
    const g = this.g;
    const w = this.barW;
    const h = this.barH;
    const ch = this.o.chamfer ?? Math.min(8, h * 0.5);
    const shape = { tl: 0, tr: 0, br: ch, bl: 0 };
    g.clear();
    g.fillStyle(this.o.bg ?? 0x070a0f, 0.85);
    g.fillPoints(chamferPoints(0, 0, w, h, shape), true);
    const fw = (w * Math.min(this.value, this.max)) / this.max;
    const gw = (w * Math.min(this.ghost, this.max)) / this.max;
    if (gw > fw) {
      g.fillStyle(this.o.ghostColor ?? 0xffffff, 0.7);
      g.fillRect(fw, 0, gw - fw, h);
    }
    if (fw > 0) {
      const col = this.flashT > 0 ? mix(this.o.color, this.flashColor, Math.min(1, this.flashT / 0.2)) : this.o.color;
      g.fillStyle(col, 1);
      if (fw >= w - ch) g.fillPoints(chamferPoints(0, 0, fw, h, { br: ch - (w - fw) }), true);
      else g.fillRect(0, 0, fw, h);
      // Top sheen + bottom shade.
      g.fillStyle(0xffffff, 0.22);
      g.fillRect(0, 0, fw, Math.max(1, Math.round(h * 0.22)));
      g.fillStyle(0x000000, 0.18);
      g.fillRect(0, h - Math.max(1, Math.round(h * 0.2)), fw, Math.max(1, Math.round(h * 0.2)));
    }
    const seg = this.o.segment ?? 0;
    if (seg > 0 && this.max / seg <= 60) {
      g.fillStyle(0x070a0f, 0.9);
      for (let v = seg; v < this.max; v += seg) {
        const sx = Math.round((w * v) / this.max);
        g.fillRect(sx - 1, 0, 2, h);
      }
    }
    g.lineStyle(1, COLORS.uiBorder, 0.9);
    g.strokePoints(chamferPoints(0, 0, w, h, shape), true, true);
  }
}

/**
 * Weapon heat gauge: orange → red with value, pulsing near overheat, hazard
 * stripes + lock while overheated. Origin = top-left.
 */
export class HeatGauge extends Phaser.GameObjects.Container {
  private readonly g: Phaser.GameObjects.Graphics;
  private heat = 0;
  private overheated = false;
  private charge = -1;
  private pulse = 0;
  readonly gw: number;
  readonly gh: number;

  constructor(scene: Phaser.Scene, x: number, y: number, w: number, h: number) {
    super(scene, x, y);
    this.gw = w;
    this.gh = h;
    this.g = scene.add.graphics();
    this.add(this.g);
    scene.add.existing(this);
  }

  /** heat 0..1; charge 0..1 for charge weapons (or <0 to hide). */
  set(heat: number, overheated: boolean, charge = -1): this {
    this.heat = Phaser.Math.Clamp(heat, 0, 1);
    this.overheated = overheated;
    this.charge = charge;
    return this;
  }

  tick(dt: number, reducedFlashing: boolean): void {
    this.pulse += dt * (this.overheated ? 7 : 10);
    const g = this.g;
    const w = this.gw;
    const h = this.gh;
    g.clear();
    g.fillStyle(0x070a0f, 0.85);
    g.fillRect(0, 0, w, h);
    const danger = this.heat > 0.75 || this.overheated;
    const wave = reducedFlashing ? 0.85 : 0.65 + 0.35 * Math.abs(Math.sin(this.pulse));
    if (this.overheated) {
      g.fillStyle(COLORS.threat, wave);
      g.fillRect(0, 0, w * Math.max(this.heat, 0.02), h);
      // Hazard stripes = locked.
      g.fillStyle(0x000000, 0.35);
      const step = 12;
      for (let sx = -h; sx < w; sx += step) {
        g.fillTriangle(sx, h, sx + h, 0, sx + h + step / 2, 0);
        g.fillTriangle(sx, h, sx + step / 2, h, sx + h + step / 2, 0);
      }
    } else if (this.heat > 0) {
      const col = mix(COLORS.heatBar, COLORS.threat, Phaser.Math.Clamp((this.heat - 0.45) / 0.5, 0, 1));
      g.fillStyle(col, danger ? wave : 1);
      g.fillRect(0, 0, w * this.heat, h);
      g.fillStyle(0xffffff, 0.25);
      g.fillRect(0, 0, w * this.heat, Math.max(1, h * 0.25));
    }
    // Overheat threshold tick.
    g.fillStyle(COLORS.threat, 0.9);
    g.fillRect(w * 0.75, 0, 1, h);
    if (this.charge >= 0) {
      g.fillStyle(0xffffff, 0.9);
      g.fillRect(0, -5, w * Phaser.Math.Clamp(this.charge, 0, 1), 3);
    }
    g.lineStyle(1, danger ? COLORS.threat : COLORS.uiBorder, danger ? 0.9 : 0.8);
    g.strokeRect(0, 0, w, h);
  }
}

// ───────────────────────────── Chips ─────────────────────────────

export interface ChipOptions {
  /** Icon texture key (resolved with fallback). */
  icon?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Prefix such as '+' or '−' drawn before the label. */
  prefix?: string;
  /** Solid tinted fill instead of outline style. */
  filled?: boolean;
  dim?: boolean;
}

/** Small chamfered label chip (origin left-centre; width set for layout). */
export function createChip(scene: Phaser.Scene, x: number, y: number, label: string, color: number, opts: ChipOptions = {}): Phaser.GameObjects.Container {
  const size = opts.size ?? 'md';
  const h = size === 'sm' ? 28 : size === 'lg' ? 40 : 34;
  const fs = size === 'sm' ? 17 : size === 'lg' ? 22 : 19;
  const c = scene.add.container(x, y);
  const g = scene.add.graphics();
  c.add(g);
  let cx = 10;
  if (opts.icon) {
    const isz = h - 12;
    const img = iconImage(scene, cx + isz / 2, 0, opts.icon, isz, color);
    c.add(img);
    cx += isz + 6;
  }
  const t = txt(scene, cx, 0, `${opts.prefix ?? ''}${label}`.toUpperCase(), {
    font: 'display',
    size: fs,
    weight: 600,
    color: opts.dim ? COLORS.uiDim : opts.filled ? 0xffffff : mix(color, 0xffffff, 0.25),
    spacing: 1.5,
    originY: 0.5,
  });
  c.add(t);
  const w = cx + t.width + 12;
  const ch = Math.round(h * 0.3);
  g.fillStyle(color, opts.filled ? 0.32 : 0.1);
  g.fillPoints(chamferPoints(0, -h / 2, w, h, { tl: ch, br: ch }), true);
  g.lineStyle(1.5, color, opts.dim ? 0.35 : 0.85);
  g.strokePoints(chamferPoints(0, -h / 2, w, h, { tl: ch, br: ch }), true, true);
  c.setSize(w, h);
  return c;
}

export function createTagChip(scene: Phaser.Scene, x: number, y: number, tag: Tag, opts: Omit<ChipOptions, 'icon'> = {}): Phaser.GameObjects.Container {
  return createChip(scene, x, y, tag, tagColor(tag), { ...opts, icon: iconKey.tag(tag) });
}

/** Lay out chips left-to-right with wrapping. Returns the total height used. */
export function flowLayout(items: Phaser.GameObjects.Container[], x: number, y: number, maxWidth: number, gap = 8, rowGap = 8): number {
  let cx = x;
  let cy = y;
  let rowH = 0;
  for (const it of items) {
    const w = it.width;
    const h = it.height;
    if (cx > x && cx + w > x + maxWidth) {
      cx = x;
      cy += rowH + rowGap;
      rowH = 0;
    }
    it.setPosition(cx, cy + h / 2);
    cx += w + gap;
    rowH = Math.max(rowH, h);
  }
  return items.length ? cy - y + rowH : 0;
}

// ───────────────────────────── Tooltip ─────────────────────────────

/** One floating tooltip per scene. */
export class Tooltip {
  private readonly c: Phaser.GameObjects.Container;
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly title: Phaser.GameObjects.Text;
  private readonly body: Phaser.GameObjects.Text;
  private readonly width: number;
  private tween: Phaser.Tweens.Tween | null = null;
  private owner: unknown = null;

  constructor(
    private readonly scene: Phaser.Scene,
    width = 420,
  ) {
    this.width = width;
    this.c = scene.add.container(0, 0).setDepth(10_000).setAlpha(0).setVisible(false);
    this.bg = scene.add.graphics();
    this.title = txt(scene, 18, 14, '', { font: 'display', size: TYPE.h3 - 2, weight: 700, spacing: 1 });
    this.body = txt(scene, 18, 50, '', { size: TYPE.small, color: COLORS.uiText, wrap: width - 36, lineSpacing: 2 });
    this.c.add([this.bg, this.title, this.body]);
  }

  /** Show near a screen point (placed to the right/below, clamped on screen). */
  show(x: number, y: number, title: string, body: string, color: number = COLORS.volt, owner?: unknown): void {
    this.owner = owner ?? null;
    this.title.setText(title.toUpperCase()).setColor(css(color));
    this.body.setText(body);
    this.body.setY(title ? 50 : 16);
    const h = (title ? 50 : 16) + this.body.height + 16;
    this.bg.clear();
    drawPanel(this.bg, 0, 0, this.width, h, { fill: COLORS.uiBg, fillAlpha: 0.96, border: color, borderAlpha: 0.8, chamfer: 10, accent: color, ticks: true });
    const px = Phaser.Math.Clamp(x + 24, 16, UI.W - this.width - 16);
    const py = Phaser.Math.Clamp(y + 18, 16, UI.H - h - 16);
    this.c.setPosition(px, py).setVisible(true);
    this.tween?.stop();
    this.tween = this.scene.tweens.add({ targets: this.c, alpha: 1, duration: 110 });
  }

  hide(owner?: unknown): void {
    if (owner !== undefined && owner !== this.owner) return;
    this.tween?.stop();
    this.tween = this.scene.tweens.add({ targets: this.c, alpha: 0, duration: 90, onComplete: () => this.c.setVisible(false) });
  }

  /** Hover a game object (made interactive) to show content. */
  attach(target: Phaser.GameObjects.GameObject, content: () => { title: string; body: string; color?: number }): void {
    if (!target.input) target.setInteractive();
    target.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, (p: Phaser.Input.Pointer) => {
      const k = content();
      this.show(p.x, p.y, k.title, k.body, k.color, target);
    });
    target.on(Phaser.Input.Events.GAMEOBJECT_POINTER_MOVE, (p: Phaser.Input.Pointer) => {
      if (this.owner !== target) return;
      const px = Phaser.Math.Clamp(p.x + 24, 16, UI.W - this.width - 16);
      this.c.setX(px);
    });
    target.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => this.hide(target));
  }
}

// ───────────────────────────── Count-up / transitions ─────────────────────────────

/** Animate a number in a text object. */
export function countUp(
  scene: Phaser.Scene,
  t: Phaser.GameObjects.Text,
  from: number,
  to: number,
  opts: { duration?: number; delay?: number; format?: (v: number) => string; onDone?: () => void; tick?: boolean } = {},
): Phaser.Tweens.Tween {
  const fmt = opts.format ?? ((v: number) => Math.round(v).toLocaleString('en-US'));
  t.setText(fmt(from));
  let last = Math.round(from);
  return scene.tweens.addCounter({
    from,
    to,
    duration: opts.duration ?? 900,
    delay: opts.delay ?? 0,
    ease: 'Cubic.easeOut',
    onUpdate: (tw) => {
      const v = tw.getValue() ?? to;
      t.setText(fmt(v));
      if (opts.tick && Math.round(v) !== last && Math.round(v) % 3 === 0) sfx('ui_hover', { volume: 0.25, pitch: 1.4 });
      last = Math.round(v);
    },
    onComplete: () => {
      t.setText(fmt(to));
      opts.onDone?.();
    },
  });
}

/** Camera fade-in for a freshly started screen. */
export function fadeIn(scene: Phaser.Scene, ms = 260): void {
  scene.cameras.main.fadeIn(ms, 5, 7, 11);
}

/** Fade out, then start another scene. Repeated calls during the fade are ignored. */
export function goTo(scene: Phaser.Scene, key: string, data?: object, ms = 240): void {
  const s = scene as Phaser.Scene & { __leaving?: boolean };
  if (s.__leaving) return;
  s.__leaving = true;
  const cam = scene.cameras.main;
  cam.fadeOut(ms, 5, 7, 11);
  cam.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
    s.__leaving = false;
    scene.scene.start(key, data);
  });
}

/** Staggered entrance: each target slides from an offset and fades in. */
export function staggerIn(
  scene: Phaser.Scene,
  targets: (Phaser.GameObjects.GameObject & { x: number; y: number; alpha: number })[],
  opts: { dx?: number; dy?: number; delay?: number; stagger?: number; duration?: number; ease?: string } = {},
): void {
  targets.forEach((t, i) => {
    const x = t.x;
    const y = t.y;
    const a = t.alpha;
    t.x = x + (opts.dx ?? 0);
    t.y = y + (opts.dy ?? 24);
    t.alpha = 0;
    scene.tweens.add({
      targets: t,
      x,
      y,
      alpha: a,
      delay: (opts.delay ?? 0) + i * (opts.stagger ?? 60),
      duration: opts.duration ?? 380,
      ease: opts.ease ?? 'Cubic.easeOut',
    });
  });
}

// ───────────────────────────── Cursor ─────────────────────────────

let cursorCss: string | null = null;
let cursorState: 'menu' | 'none' | null = null;

/** Neon arrow cursor drawn once into a data URL (the canvas hides the OS cursor during play). */
function menuCursorCss(): string {
  if (cursorCss) return cursorCss;
  try {
    const cv = document.createElement('canvas');
    cv.width = 32;
    cv.height = 32;
    const c = cv.getContext('2d');
    if (!c) throw new Error('no 2d');
    c.beginPath();
    c.moveTo(3, 2);
    c.lineTo(3, 24);
    c.lineTo(9, 18.5);
    c.lineTo(13.5, 28);
    c.lineTo(17.5, 26);
    c.lineTo(13, 17);
    c.lineTo(21, 16);
    c.closePath();
    c.fillStyle = '#0b0e14';
    c.fill();
    c.lineWidth = 2;
    c.strokeStyle = css(COLORS.volt);
    c.stroke();
    c.fillStyle = 'rgba(230,237,245,0.95)';
    c.beginPath();
    c.moveTo(5.5, 7);
    c.lineTo(5.5, 18.5);
    c.lineTo(9.5, 15);
    c.lineTo(15, 14.5);
    c.closePath();
    c.fill();
    cursorCss = `url(${cv.toDataURL('image/png')}) 3 2, default`;
  } catch {
    cursorCss = 'default';
  }
  return cursorCss;
}

/** Show the menu cursor (true) or hide it for gameplay (false). */
export function setMenuCursor(scene: Phaser.Scene, on: boolean): void {
  const want = on ? 'menu' : 'none';
  if (cursorState === want) return;
  cursorState = want;
  try {
    scene.input.setDefaultCursor(on ? menuCursorCss() : 'none');
  } catch {
    // ignore
  }
}

/** Menu cursor whenever the Game scene is not actively running (paused overlays, menus). */
export function syncCursor(scene: Phaser.Scene): void {
  const playing = scene.scene.isActive(SCENES.GAME);
  setMenuCursor(scene, !playing);
}

// ───────────────────────────── Confirm dialog ─────────────────────────────

export interface ConfirmOptions {
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  accent?: number;
  /** Navigation of the screen underneath (disabled while the dialog is open). */
  parentNav?: MenuNav;
  onConfirm: () => void;
  onCancel?: () => void;
}

/** Modal yes/no dialog with its own focus navigation. Defaults focus to Cancel. */
export function confirmDialog(scene: Phaser.Scene, o: ConfirmOptions): { close: () => void } {
  const accent = o.danger ? COLORS.uiBad : (o.accent ?? COLORS.volt);
  const root = scene.add.container(0, 0).setDepth(9000);
  const dim = backdrop(scene, 0.7);
  root.add(dim);
  const w = 760;
  const bodyText = txt(scene, 0, 0, o.body, { size: TYPE.body, color: COLORS.uiText, wrap: w - 96, lineSpacing: 4 });
  const h = 150 + bodyText.height + 110;
  const x = (UI.W - w) / 2;
  const y = (UI.H - h) / 2;
  const panel = new Panel(scene, x, y, w, h, { fill: COLORS.uiBg, fillAlpha: 0.97, border: accent, borderAlpha: 0.9, accent, ticks: true, glow: accent, glowAlpha: 0.4, chamfer: 18, header: 72, headerAlpha: 0.14 });
  const title = txt(scene, x + 48, y + 22, o.title.toUpperCase(), { font: 'display', size: TYPE.h2, weight: 700, color: accent, spacing: 3 });
  bodyText.setPosition(x + 48, y + 104);
  root.add([panel, title, bodyText]);

  const parent = o.parentNav;
  if (parent) parent.enabled = false;
  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    nav.destroy();
    scene.tweens.add({
      targets: root,
      alpha: 0,
      duration: 120,
      onComplete: () => root.destroy(),
    });
    if (parent) parent.enabled = true;
  };
  const nav = new MenuNav(scene, {
    layout: 'horizontal',
    onBack: () => {
      sfx('ui_back');
      close();
      o.onCancel?.();
    },
  });
  const by = y + h - 64;
  const cancel = createButton(scene, UI.W / 2 - 170, by, { label: o.cancelLabel ?? 'Cancel', width: 300, height: 60, align: 'center', accent: COLORS.uiDim, sound: 'ui_back', onClick: () => { close(); o.onCancel?.(); } }, nav);
  const ok = createButton(scene, UI.W / 2 + 170, by, { label: o.confirmLabel ?? 'Confirm', width: 300, height: 60, align: 'center', accent, variant: o.danger ? 'danger' : 'primary', onClick: () => { close(); o.onConfirm(); } }, nav);
  root.add([cancel, ok]);
  nav.focus(cancel, { silent: true });
  nav.lock(200);
  // Poll the gamepad for this layer.
  const tick = (): void => nav.update();
  scene.events.on(Phaser.Scenes.Events.UPDATE, tick);
  root.once(Phaser.GameObjects.Events.DESTROY, () => scene.events.off(Phaser.Scenes.Events.UPDATE, tick));
  root.setAlpha(0);
  scene.tweens.add({ targets: root, alpha: 1, duration: 140 });
  panel.setScale(1, 0.96);
  scene.tweens.add({ targets: panel, scaleY: 1, duration: 180, ease: 'Back.easeOut' });
  return { close };
}

/** Footer hint bar helper (bottom of the screen). */
export function footerHints(scene: Phaser.Scene, entries: { action: UiAction; label: string }[], align: 'left' | 'center' | 'right' = 'right'): HintBar {
  const x = align === 'left' ? UI.margin : align === 'center' ? UI.W / 2 : UI.W - UI.margin;
  return new HintBar(scene, x, UI.H - 44, entries, { align, size: 30 });
}

/** Thin horizontal rule with fading ends. */
export function hairline(scene: Phaser.Scene, x: number, y: number, w: number, color: number = COLORS.uiBorder, alpha = 1): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics();
  const steps = 12;
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const a = alpha * Math.min(1, Math.min(t, 1 - t) * 4);
    g.fillStyle(color, a);
    g.fillRect(x + (w * i) / steps, y, w / steps + 0.5, 1);
  }
  return g;
}
